import { useState, useCallback } from "react";
import { RESIZE_CONFIG } from "../constants/constants";

interface ResizablePanelAPI {
  width: number;
  startResizing: (e: React.MouseEvent, classes: any) => void;
}

/**
 * Provides a width state and a callback to enable resizing of a panel via
 * mouse events.
 *
 * @param {number} [initialWidth = RESIZE_CONFIG.DEFAULT_PANEL_WIDTH]
 *   The initial width of the panel.
 * @returns {ResizablePanelAPI} An object containing the panel width state and
 *   a callback to start resizing the panel.
 */
export const useResizablePanel = (
  initialWidth = RESIZE_CONFIG.DEFAULT_PANEL_WIDTH
): ResizablePanelAPI => {
  const [width, setWidth] = useState<number>(initialWidth);

  const startResizing = useCallback(
    (e: React.MouseEvent, classes: any) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;
      const container = e.currentTarget.parentElement;
      const containerWidth = container?.offsetWidth ?? window.innerWidth;

      // Create persistent overlay
      const overlay = document.createElement("div");
      overlay.className = classes.resizeOverlay;
      document.body.appendChild(overlay);

      // Store original styles and lock body
      const originalCursor = document.body.style.cursor;
      const originalUserSelect = document.body.style.userSelect;
      const originalPointerEvents = document.body.style.pointerEvents;

      // need to use setProperty because writing to variables defined outside component or hook is not allowed in React
      document.body.style.setProperty("cursor", "col-resize");
      document.body.style.setProperty("user-select", "none");
      document.body.style.setProperty("pointer-events", "none");

      const doDrag = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.min(
          Math.max(
            RESIZE_CONFIG.MIN_PANEL_WIDTH,
            startWidth + (delta / containerWidth) * 100
          ),
          RESIZE_CONFIG.MAX_PANEL_WIDTH
        );
        setWidth(newWidth);
      };

      const stopDrag = () => {
        // Cleanup overlay and styles
        document.body.removeChild(overlay);
        document.body.style.setProperty("cursor", originalCursor);
        document.body.style.setProperty("user-select", originalUserSelect);
        document.body.style.setProperty(
          "pointer-events",
          originalPointerEvents
        );
        document.removeEventListener("mousemove", doDrag);
        document.removeEventListener("mouseup", stopDrag);
      };

      // Use passive events for better performance
      document.addEventListener("mousemove", doDrag, { passive: true });
      document.addEventListener("mouseup", stopDrag, { passive: true });

      // Force cursor style on the overlay
      overlay.style.cursor = "col-resize";
    },
    [width]
  );

  return { width, startResizing };
};
